import AttachFileIcon from "@mui/icons-material/AttachFile"
import CloseIcon from "@mui/icons-material/Close"
import SendIcon from "@mui/icons-material/Send"
import StopCircleIcon from "@mui/icons-material/StopCircle"
import {
  Box,
  Chip,
  IconButton,
  iconButtonClasses,
  InputAdornment,
  styled,
  TextField,
} from "@mui/material"
import type React from "react"
import { useRef, useState } from "react"
import { extraColors } from "../../utils/theme"

const StyledTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: extraColors.dark5,
    color: extraColors.white,
    borderRadius: "8px",
    "& fieldset": {
      borderColor: extraColors.translucentWhite,
    },
    "&:hover fieldset": {
      borderColor: extraColors.status.navyLight,
    },
    "&.Mui-focused fieldset": {
      borderColor: extraColors.status.navyLight,
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: extraColors.medium,
    opacity: 1,
  },
}))

interface ChatInputProps {
  onSend: (query: string) => void
  disabled?: boolean
  isStreaming?: boolean
  onStop?: () => void
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disabled,
  isStreaming,
  onStop,
}) => {
  const [input, setInput] = useState("")
  const [files, setFiles] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleSend = () => {
    if (disabled) return
    if (input.trim() || files.length > 0) {
      onSend(input)
      setInput("")
      setFiles([])
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files
    if (selectedFiles) {
      setFiles(prev => [...prev, ...Array.from(selectedFiles)])
    }
    // Clear input value to allow selecting same file again
    e.target.value = ""
  }

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      if (!disabled) {
        handleSend()
      }
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {files.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
          {files.map((file, index) => (
            <Chip
              key={`${file.name}-${index}`}
              label={file.name}
              onDelete={() => removeFile(index)}
              deleteIcon={<CloseIcon sx={{ fontSize: "14px !important" }} />}
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.05)",
                color: extraColors.white,
                borderRadius: "4px",
                height: "24px",
                "& .MuiChip-deleteIcon": {
                  color: extraColors.medium,
                  "&:hover": { color: extraColors.white },
                },
              }}
            />
          ))}
        </Box>
      )}
      <Box sx={{ display: "flex", gap: 1 }}>
        <input
          type="file"
          multiple
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
        <StyledTextField
          fullWidth
          multiline
          maxRows={4}
          variant="outlined"
          placeholder="Message Wingman..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled}
                    sx={{
                      color: extraColors.medium,
                      "&:hover": { color: extraColors.white },
                    }}
                  >
                    <AttachFileIcon />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {isStreaming ? (
                    <IconButton
                      onClick={onStop}
                      sx={{
                        color: extraColors.status.navyLight,
                        "&:hover": { color: "error.main" },
                      }}
                    >
                      <StopCircleIcon />
                    </IconButton>
                  ) : (
                    <IconButton
                      onClick={handleSend}
                      disabled={
                        disabled || (!input.trim() && files.length === 0)
                      }
                      sx={{
                        color: extraColors.status.navyLight,
                        [`&.${iconButtonClasses.disabled}`]: {
                          color: extraColors.dark10,
                        },
                      }}
                    >
                      <SendIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
    </Box>
  )
}
